/* eslint-disable no-nested-ternary */
import { useTranslate } from 'hooks/useTranslate';
import { Tooltip } from 'react-tooltip';
import { formatNumber } from 'utils/formats';

import '../PartnerBook.scss';

const PlanSummaryMainTable = ({
  view,
  summary,
  todaySummary,
  plansNames,
  isFormatted = true,
  isTodaySummaryVisible = true,
}) => {
  const { t } = useTranslate();

  const formatValue = (value) => (isFormatted ? formatNumber(value) : value);

  const calculateTotals = () => {
    const totals = [0, 0, 0, 0, 0, 0];

    // Sum phantoms
    Object.keys(summary.phantoms || {}).forEach((planId) => {
      Object.values(summary.phantoms[planId]).forEach((value, index) => {
        totals[index] += Number(value) || 0;
      });
    });

    // Sum stockOptions
    Object.keys(summary.stockOptions || {}).forEach((planId) => {
      Object.values(summary.stockOptions[planId]).forEach((value, index) => {
        totals[index] += Number(value) || 0;
      });
    });

    // Sum warrants
    Object.keys(summary.warrants || {}).forEach((planId) => {
      Object.values(summary.warrants[planId]).forEach((value, index) => {
        totals[index] += Number(value) || 0;
      });
    });

    return totals;
  };

  const totals = calculateTotals();

  // Column keys for the totals row
  const columnKeys = [
    'totalUnits',
    'assignedUnits',
    'available',
    'vested',
    'canceledUnits',
    'acquired',
  ];

  return (
    summary && (
      <div className={view === 2 ? 'nk-block' : 'd-none'}>
        <div className="card card-bordered card-preview">
          <div className="table-responsive">
            <table
              className="table table-striped-inverse table-compact nk-tb-list nk-tb-ulist border-right "
              id="summaryTable"
            >
              <thead>
                <tr className="nk-tb-item nk-tb-head tb-tnx-head text-center">
                  <th className="nk-tb-col w-200px">{t('Plans')}</th>
                  <th className="nk-tb-col no-break">{t('TotalUnits')}</th>
                  <th className="nk-tb-col no-break">{t('AssignedUnits')}</th>
                  <th className="nk-tb-col no-break">{t('Available')}</th>
                  <th className="nk-tb-col no-break">{t('Vested')}</th>
                  <th className="nk-tb-col no-break">{t('CanceledUnits')}</th>
                  <th className="nk-tb-col no-break">{t('Acquired')}</th>
                </tr>
              </thead>

              <tbody>
                {Object.keys(summary.phantoms)?.map((planId) => (
                  <>
                    <tr className="nk-tb-item">
                      <td
                        id={planId}
                        className="nk-tb-col text-left w-200px text-ellipsis border-right"
                      >
                        {plansNames[planId]}
                        <Tooltip anchorId={planId} place="bottom">
                          {plansNames[planId]}
                        </Tooltip>
                      </td>

                      {Object.entries(summary.phantoms[planId]).map(
                        ([key, value], index) => {
                          const otherValue = todaySummary?.[planId]?.[key];

                          return (
                            <td
                              key={key}
                              className={
                                index === 0
                                  ? 'nk-tb-col text-right text-ellipsis border-right'
                                  : 'nk-tb-col text-right border-right'
                              }
                            >
                              <b>{formatValue(value)}</b>
                              {isTodaySummaryVisible && (
                                <span className="d-block">
                                  ({formatValue(otherValue)})
                                </span>
                              )}
                            </td>
                          );
                        }
                      )}
                    </tr>
                  </>
                ))}

                {Object.keys(summary.stockOptions)?.map((planId) => (
                  <tr className="nk-tb-item">
                    <td
                      id={planId}
                      className="nk-tb-col text-left w-200px text-ellipsis border-right"
                    >
                      {plansNames[planId]}
                      <Tooltip anchorId={planId} place="bottom">
                        {plansNames[planId]}
                      </Tooltip>
                    </td>

                    {Object.entries(summary.stockOptions[planId]).map(
                      ([key, value], index) => {
                        const otherValue = todaySummary?.[planId]?.[key];

                        return (
                          <td
                            key={key}
                            className={
                              index === 0
                                ? 'nk-tb-col text-right text-ellipsis border-right'
                                : 'nk-tb-col text-right border-right'
                            }
                          >
                            <b>{formatValue(value)}</b>
                            {isTodaySummaryVisible && (
                              <span className="d-block">
                                ({formatValue(otherValue)})
                              </span>
                            )}
                          </td>
                        );
                      }
                    )}
                  </tr>
                ))}

                {Object.keys(summary.warrants)?.map((planId) => (
                  <tr className="nk-tb-item">
                    <td
                      id={planId}
                      className="nk-tb-col text-left w-200px text-ellipsis border-right"
                    >
                      {plansNames[planId]}
                      <Tooltip anchorId={planId} place="bottom">
                        {plansNames[planId]}
                      </Tooltip>
                    </td>

                    {Object.entries(summary.warrants[planId]).map(
                      ([key, value], index) => {
                        const otherValue = todaySummary?.[planId]?.[key];

                        return (
                          <td
                            key={key}
                            className={
                              index === 0
                                ? 'nk-tb-col text-right text-ellipsis border-right'
                                : 'nk-tb-col text-right border-right'
                            }
                          >
                            <b>{formatValue(value)}</b>
                            {isTodaySummaryVisible && (
                              <span className="d-block">
                                ({formatValue(otherValue)})
                              </span>
                            )}
                          </td>
                        );
                      }
                    )}
                  </tr>
                ))}
              </tbody>

              {/* Total Row */}
              <tfoot>
                <tr className="nk-tb-item tb-tnx-head fs-11px font-weight-bold border-top">
                  <td className="nk-tb-col text-left w-200px border-right">
                    {t('Total')}
                  </td>
                  {totals.map((total, index) => (
                    <td
                      key={columnKeys[index]}
                      className="nk-tb-col text-right border-right"
                    >
                      {formatValue(total)}
                    </td>
                  ))}
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>
    )
  );
};

export default PlanSummaryMainTable;
